<template>
  <el-card>
    <div slot="header">客服员工管理</div>
    <p style="margin:0 0 12px;font-size:13px;color:#909399;line-height:1.5;">
      「昵称」即访客在网站嵌入客服窗口中看到的客服称呼（顶栏与对话气泡头像旁）；请填写便于识别的名称。
    </p>
    <el-form :model="form" inline>
      <el-form-item label="账号"><el-input v-model="form.username" /></el-form-item>
      <el-form-item label="昵称"><el-input v-model="form.display_name" /></el-form-item>
      <el-form-item label="密码"><el-input v-model="form.password" type="password" /></el-form-item>
      <el-form-item label="并发"><el-input-number v-model="form.max_concurrent" :min="1" /></el-form-item>
      <el-form-item><el-button type="primary" @click="addAgent">新增</el-button></el-form-item>
    </el-form>

    <el-table :data="list">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="username" label="账号" min-width="120" />
      <el-table-column label="昵称" min-width="140">
        <template slot-scope="{ row }">
          <el-input v-model="row.display_name" size="small" placeholder="访客可见昵称" />
        </template>
      </el-table-column>
      <el-table-column label="名下会话" width="110" align="center">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="row.active_conversation_count > 0 ? 'warning' : 'info'">
            {{ row.active_conversation_count || 0 }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="新密码" min-width="160">
        <template slot-scope="{ row }">
          <el-input v-model="pwdMap[row.id]" size="small" type="password" placeholder="留空不修改" show-password />
        </template>
      </el-table-column>
      <el-table-column label="并发上限" width="140">
        <template slot-scope="{ row }">
          <el-input-number v-model="row.max_concurrent" size="small" :min="1" controls-position="right" style="width:110px;" />
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template slot-scope="{ row }">
          <el-switch v-model="row.status" :active-value="1" :inactive-value="0" @change="save(row)" />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="primary" size="mini" plain @click="save(row)">保存</el-button>
          <el-button
            type="warning"
            size="mini"
            plain
            :disabled="!row.active_conversation_count"
            @click="openTransfer(row)"
          >一键转接</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog title="一键转接会话" :visible.sync="transferVisible" width="480px">
      <div v-if="transferFrom">
        <p style="margin:0 0 12px;line-height:1.6;color:#606266;">
          将客服 <strong>{{ transferFrom.display_name }}</strong>（{{ transferFrom.username }}）
          名下 <strong>{{ transferFrom.active_conversation_count || 0 }}</strong> 个会话（含已结束），
          全部转接给：
        </p>
        <el-select v-model="transferToId" placeholder="选择接收客服" style="width:100%;">
          <el-option
            v-for="item in transferTargetOptions"
            :key="item.id"
            :label="item.display_name + '（' + item.username + '）'"
            :value="item.id"
          />
        </el-select>
        <p style="margin:12px 0 0;font-size:12px;color:#909399;line-height:1.5;">
          包含进行中、排队中、已结束的会话。进行中的会话转接后访客侧会切换到新客服；已结束会话仅变更归属记录，不会重新打开对话。
        </p>
      </div>
      <span slot="footer">
        <el-button @click="transferVisible=false">取消</el-button>
        <el-button type="primary" :loading="transferLoading" :disabled="!transferToId" @click="confirmTransfer">确认转接</el-button>
      </span>
    </el-dialog>
  </el-card>
</template>

<script>
import { createAgent, listAgents, updateAgent, transferAllConversations } from '../api/merchant';

export default {
  data() {
    return {
      list: [],
      form: { username: '', display_name: '', password: '', max_concurrent: 5 },
      pwdMap: {},
      transferVisible: false,
      transferFrom: null,
      transferToId: null,
      transferLoading: false
    };
  },
  computed: {
    transferTargetOptions() {
      if (!this.transferFrom) return [];
      return this.list.filter(
        item => item.id !== this.transferFrom.id && item.status === 1
      );
    }
  },
  methods: {
    async load() {
      const { data } = await listAgents();
      this.list = data.data || [];
      this.list.forEach(v => this.$set(this.pwdMap, v.id, ''));
    },
    async addAgent() {
      await createAgent(this.form);
      this.form = { username: '', display_name: '', password: '', max_concurrent: 5 };
      await this.load();
    },
    async save(row) {
      const pwd = String(this.pwdMap[row.id] || '').trim();
      const payload = {
        display_name: row.display_name,
        max_concurrent: row.max_concurrent,
        status: row.status
      };
      if (pwd) payload.password = pwd;
      await updateAgent(row.id, payload);
      if (pwd) this.$set(this.pwdMap, row.id, '');
      this.$message.success('已保存');
      await this.load();
    },
    openTransfer(row) {
      this.transferFrom = row;
      this.transferToId = null;
      this.transferVisible = true;
    },
    async confirmTransfer() {
      if (!this.transferFrom || !this.transferToId) return;
      try {
        await this.$confirm(
          `确定将 ${this.transferFrom.display_name} 的全部会话（含已结束）转接给所选客服吗？`,
          '确认转接',
          { type: 'warning' }
        );
      } catch (e) {
        return;
      }
      this.transferLoading = true;
      try {
        const { data } = await transferAllConversations(this.transferFrom.id, this.transferToId);
        const result = data.data || {};
        this.$message.success(`已成功转接 ${result.transferred_count || 0} 个会话`);
        this.transferVisible = false;
        await this.load();
      } catch (e) {
        this.$message.error((e.response && e.response.data && e.response.data.message) || '转接失败');
      } finally {
        this.transferLoading = false;
      }
    }
  },
  created() {
    this.load();
  }
};
</script>
